import {
  IFirmwareFileInspection,
  IFirmwareImageInspection,
} from '../../api/types';

function formatHex(value: number | null): string {
  return value === null
    ? 'Not available'
    : `0x${value.toString(16).toUpperCase().padStart(8, '0')}`;
}

function formatValue(
  value: number | string | boolean | null,
): string {
  return value === null ? 'Not available' : value.toString();
}

function InspectionImageDetails({
  image,
}: {
  image: IFirmwareImageInspection;
}) {
  const hasAdditionalMetadata =
    image.imageId !== null ||
    image.softwareId !== null ||
    image.imageType !== null ||
    image.uncompressedSize !== null ||
    image.firmwareLoadAddress != null ||
    image.md5 !== null ||
    image.headerCrcValid !== null ||
    image.applicationCrcValid !== null ||
    image.warnings.length > 0;

  if (!hasAdditionalMetadata) return null;

  return (
    <details className="mt-2 text-sm">
      <summary className="cursor-pointer">
        Additional image metadata
      </summary>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2">
        {image.imageId !== null && (
          <>
            <span>Image ID</span>
            <span>{formatHex(image.imageId)}</span>
          </>
        )}
        {image.softwareId !== null && (
          <>
            <span>Software ID</span>
            <span>{formatValue(image.softwareId)}</span>
          </>
        )}
        {image.imageType !== null && (
          <>
            <span>Image type</span>
            <span>{formatValue(image.imageType)}</span>
          </>
        )}
        {image.firmwareLoadAddress != null && (
          <>
            <span>Firmware load address</span>
            <span>{formatHex(image.firmwareLoadAddress)}</span>
          </>
        )}
        {image.uncompressedSize !== null && (
          <>
            <span>Uncompressed size</span>
            <span>{formatValue(image.uncompressedSize)}</span>
          </>
        )}
        {image.md5 !== null && (
          <>
            <span>MD5</span>
            <span className="break-all">
              {formatValue(image.md5)}
            </span>
          </>
        )}
        {image.headerCrcValid !== null && (
          <>
            <span>Header CRC</span>
            <span>{formatValue(image.headerCrcValid)}</span>
          </>
        )}
        {image.applicationCrcValid !== null && (
          <>
            <span>Application CRC</span>
            <span>{formatValue(image.applicationCrcValid)}</span>
          </>
        )}
      </div>
      {image.warnings.length > 0 && (
        <ul className="list-disc ml-5 mt-2 text-amber-600 dark:text-amber-400">
          {image.warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      )}
    </details>
  );
}

export function FirmwareInspectionPanel({
  inspection,
}: {
  inspection: IFirmwareFileInspection;
}) {
  return (
    <div
      className="border border-gray-300 rounded p-3 text-sm"
      data-testid="firmware-inspection"
    >
      <h2 className="font-semibold mb-2">Detected file metadata</h2>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        <span>Format</span>
        <span>{inspection.format}</span>
        <span>File length</span>
        <span>{inspection.fileLength} bytes</span>
        <span>Declared length</span>
        <span>{formatValue(inspection.declaredLength)}</span>
        <span>Length matches</span>
        <span>{formatValue(inspection.lengthMatches)}</span>
        <span>Suggested FWM firmware version</span>
        <span>
          {formatValue(inspection.suggestedFirmwareVersion)}
        </span>
        <span>Suggested semantic version</span>
        <span>
          {formatValue(inspection.suggestedSemanticVersion)}
        </span>
        <span>Blob key ID</span>
        <span>{formatHex(inspection.blobKeyId)}</span>
        <span>Software ID</span>
        <span>{formatHex(inspection.softwareId)}</span>
        <span>Image count</span>
        <span>{formatValue(inspection.imageCount)}</span>
        <span>Signature status</span>
        <span>{inspection.signatureStatus}</span>
        {inspection.legacyControlBlock && (
          <>
            <span>Legacy firmware version ID</span>
            <span>
              {inspection.legacyControlBlock.firmwareVersionId}
            </span>
            <span>Legacy image CRC</span>
            <span>
              {formatHex(inspection.legacyControlBlock.imageCrc)}
            </span>
            <span>Legacy control block CRC</span>
            <span>
              {formatHex(inspection.legacyControlBlock.blockCrc)}
            </span>
            <span>Control block CRC valid</span>
            <span>
              {formatValue(
                inspection.legacyControlBlock.blockCrcValid,
              )}
            </span>
            <span>Legacy image CRC valid</span>
            <span>
              {formatValue(
                inspection.legacyControlBlock.imageCrcValid,
              )}
            </span>
          </>
        )}
      </div>

      {inspection.images.length > 0 && (
        <div className="overflow-x-auto mt-3">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b">
                <th className="pr-3 py-1">Target</th>
                <th className="pr-3 py-1">Version</th>
                <th className="pr-3 py-1">Length</th>
                <th className="pr-3 py-1">Address</th>
                <th className="pr-3 py-1">Key ID</th>
                <th className="py-1">Signature</th>
              </tr>
            </thead>
            <tbody>
              {inspection.images.map((image, index) => (
                <tr
                  className="border-b align-top"
                  key={`${image.imageId ?? 'image'}-${index}`}
                >
                  <td className="pr-3 py-1">
                    {image.mcuName ?? formatHex(image.imageId)}
                  </td>
                  <td className="pr-3 py-1">
                    {formatValue(image.semanticVersion)}
                  </td>
                  <td className="pr-3 py-1">
                    {formatValue(image.length)}
                  </td>
                  <td className="pr-3 py-1">
                    {formatHex(image.startAddress)}
                  </td>
                  <td className="pr-3 py-1">
                    {formatHex(image.keyId)}
                  </td>
                  <td className="py-1">
                    {image.signatureStatus}
                    <InspectionImageDetails image={image} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {inspection.warnings.length > 0 && (
        <div className="mt-3 text-amber-600 dark:text-amber-400">
          <p className="font-semibold">Warnings</p>
          <ul className="list-disc ml-5">
            {inspection.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
